/**
 * §H5 change — "epoch selects (from available land-cover versions; default 2020→2021) ·
 * extent (country scope | AOI)"; result "transition matrix table (diagonal tinted
 * brand-tint; off-diagonal >2.5% tinted amber), net-change table (± coloured with km²)".
 *
 * **The epochs come from §D5**, not from a route of this tool's own: `/layers` already
 * carries `epoch` and is already access-filtered, so `epochRasters` is the whole catalogue
 * of epochs this reader may cross-tabulate (T6). Two selects over one list, defaulting to
 * the oldest and the newest, which today is exactly 2020 → 2021.
 *
 * **Client item 5, 2026-09-16: the extent is the operational hierarchy.** §H5 wrote the
 * extent as "country scope | AOI" and the form could only offer those two, because it read
 * the §F5.8 cascade rather than asking a question of its own. But `geom_from_params` has
 * always taken a `unit_id` for *any* of §C's three branches, so the control that was
 * missing is a picker: a rung (country / region / commune / watershed / partnership /
 * drawn area) and the unit on it. Three consequences, all deliberate:
 *
 * * **the opening position is the map's scope, and only the opening position.** A reader
 *   who filtered to Bale and opened the tool means Bale; a reader who then picked a
 *   watershed here means the watershed, and the cascade moving underneath them afterwards
 *   would be a control changing itself (`initialExtent`).
 * * **the drawn area is listed with nothing drawn, disabled.** An option that vanishes
 *   reads as a feature that is missing.
 * * **the request body is unchanged** — `unit_id` | `aoi_polygon` | `country` | `{}`, the
 *   four shapes §E7 already resolves. Nothing new was asked of the server for this.
 *
 * **Both epochs travel or neither.** §H5 refuses one alone with a sentence; sending both
 * always is what keeps that refusal impossible from this form.
 *
 * **The overlay is asked for, not imposed** (client item 9). §H5's job now writes a PNG of
 * the changed pixels, coloured by the class they became, and the reader adds it with a
 * button — the §H7/§H8 pattern. Rearranging the map under someone who came for a table is
 * the one thing a result panel must not do, and the button is one click either way.
 *
 * "Show change on map" **fits the map to the result** as well as registering it
 * (`state.fitRequest`): a result the reader asked to see can be a hundred kilometres off
 * the current view, and a button that says "on map" has not kept its word until the map
 * shows it.
 *
 * `image.changed_pct` is printed as "(sampled)" because it is: on a decimated run it is an
 * even sample of the same extent rather than a census, and the matrix above it is the exact
 * figure. Saying so is cheaper than a reader finding the two numbers a tenth apart.
 *
 * The label the store keeps is **this panel's**, not the server's: `apps/analysis` has no
 * translation table for prose, so the spec carries an English fallback and the localised
 * "Land-cover change 2020 → 2021" is built here, where both years and both languages are.
 *
 * `coverage.pct` can read a little over 100 — whole masked pixels against an exact
 * spheroidal polygon area (T6's parked note) — so the panel prints "100 %" for anything at
 * or above it rather than the honest-but-alarming "100.4 % covered".
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  aoiRing,
  useToolRun,
  type ChangeResult,
  type ExtentDescription,
} from '../../../lib/analysis';
import { fetchUnitOptions, queryKeys, type UnitOption, type UnitType } from '../../../lib/api';
import { fmtKm2, fmtNum } from '../../../lib/format';
import { Icon } from '../../../lib/icons';
import { COUNTRY_NAMES, useAppStore, type Country, type Scope } from '../../../lib/state';
import { toast } from '../../../lib/toast';
import type { ToolProps } from '../AnalysisDock';
import { ToolFrame } from '../ToolFrame';
import { ResultBox, ResultNote } from '../ResultBox';
import { epochRasters, useAnalysisLayers } from '../pickers';

/** §H5: "off-diagonal >2.5% tinted amber". */
export const WARM_THRESHOLD = 2.5;

/**
 * The six extents §E7 can actually be asked for, in the order a reader widens or narrows.
 *
 * `admin1`/`admin2` are not two levels of one control the way §H1's are: they travel as a
 * `unit_id` exactly as a watershed does, and `geom_from_params` has never cared which
 * branch the id came from. So the kind *is* the rung, and what changes between them is only
 * which list the id is picked from.
 */
export type ExtentKind = 'country' | 'admin1' | 'admin2' | 'watershed' | 'partnership' | 'aoi';

const EXTENT_KINDS: ExtentKind[] = [
  'country',
  'admin1',
  'admin2',
  'watershed',
  'partnership',
  'aoi',
];

/**
 * What to call each rung. The admin three are §F1's own level words, so the dock names a
 * region as the map does.
 *
 * The two flat branches get **singular** labels of their own rather than Compare's
 * `TYPE_KEY` plurals (T7 review 4): §H1's segment picks a *branch* to list units from —
 * "Watersheds" — and this picks one unit, so "Watershed" is the honest word both above the
 * select and inside "Extent: Mouhoun (Watershed)".
 */
const KIND_KEY: Record<ExtentKind, string> = {
  country: 'indicators.levelCountry',
  admin1: 'indicators.levelRegion',
  admin2: 'indicators.levelCommune',
  watershed: 'analysis.change.extentWatershed',
  partnership: 'analysis.change.extentPartnership',
  aoi: 'analysis.change.extentAoi',
};

/**
 * `extent.kind` as the server words it (`geometry.ExtentDescription`) → a label.
 *
 * The server says `unit` for all three branches — it resolves an id and never records which
 * tree it came from — so `unit` is deliberately absent here and answered from the rung the
 * run was launched on. Anything unknown falls through to the raw kind rather than to an
 * empty parenthesis.
 */
const RESULT_KIND_KEY: Record<string, string> = {
  country: KIND_KEY.country,
  watershed: KIND_KEY.watershed,
  partnership: KIND_KEY.partnership,
};

/**
 * The two extents whose *name* the server writes in English prose — `"Drawn area"` and
 * `"Programme"` (`geometry.geom_from_params`). §E7 has no translation table, so those two
 * are named here instead, and the parenthetical kind is dropped with them: "Extent: Drawn
 * AOI (Drawn AOI)" is the kind of line that makes a reader distrust the rest of the panel.
 * A unit and a country keep the server's label, which is a place name in either language.
 */
const SELF_NAMING_KIND: Record<string, string> = {
  aoi: KIND_KEY.aoi,
  programme: 'analysis.change.programme',
};

/** The countries the extent can be pointed at — `ALL` being §E7's programme extent. */
const EXTENT_COUNTRIES = Object.keys(COUNTRY_NAMES) as Country[];

/** Everything the picker holds: the rung, and the pick on each list that rung can use. */
export interface ExtentPick {
  kind: ExtentKind;
  country: Country;
  region: string;
  commune: string;
  /** A watershed or a partnership id — one slot, because only one of the two is ever on. */
  unit: string;
}

/**
 * The picker's opening position, **derived once**.
 *
 * A reader who filtered the map to Bale and opened this tool means Bale, so the scope is
 * read — but only at that moment. Tracking the cascade after that would move a control the
 * reader is using, and §F5.8's filter and §H5's extent are two different questions the
 * moment the second one is asked.
 */
function initialExtent(scope: Scope | null, country: Country): ExtentPick {
  const base: ExtentPick = { kind: 'country', country, region: '', commune: '', unit: '' };
  if (!scope) return base;
  const from = scope.country && scope.country in COUNTRY_NAMES ? (scope.country as Country) : country;
  if (scope.unitType === 'watershed' || scope.unitType === 'partnership') {
    return { ...base, kind: scope.unitType, country: from, unit: scope.unitId };
  }
  if (scope.adminLevel === 1) return { ...base, kind: 'admin1', country: from, region: scope.unitId };
  if (scope.adminLevel === 2) {
    return {
      ...base,
      kind: 'admin2',
      country: from,
      region: scope.parentId ?? '',
      commune: scope.unitId,
    };
  }
  return { ...base, country: from };
}

export function Change({ onBack }: ToolProps) {
  const { t } = useTranslation();
  const aoi = useAppStore((s) => s.aoi);
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const fitBounds = useAppStore((s) => s.fitBounds);

  const layers = useAnalysisLayers();
  const epochs = useMemo(() => epochRasters(layers), [layers]);
  const [epochA, setEpochA] = useState('');
  const [epochB, setEpochB] = useState('');
  // `getState` rather than a subscription: the opening position is the scope at the moment
  // the form was opened, and nothing after it (see `initialExtent`).
  const [ext, setExt] = useState<ExtentPick>(() => {
    const { scope, country } = useAppStore.getState();
    return initialExtent(scope, country);
  });
  /** The rung the *held result* was run on — Compare's `ranType` rule, for the extent line. */
  const [ranKind, setRanKind] = useState<ExtentKind>(ext.kind);
  const run = useToolRun<ChangeResult>('change');

  const a = epochA || epochs[0]?.id || '';
  const b = epochB || epochs[epochs.length - 1]?.id || '';

  const onAdmin = ext.kind === 'admin1' || ext.kind === 'admin2';
  const onFlat = ext.kind === 'watershed' || ext.kind === 'partnership';
  /** Which flat branch the single unit select is listing. Stable so its key is too. */
  const flatType: UnitType = ext.kind === 'partnership' ? 'partnership' : 'watershed';

  /**
   * `operational: false` on all three, for §H1's reason: the §F5.8 cascade lists only units
   * with reported MELS activity because that is what its map paints, and a land-cover
   * cross-tabulation has an answer for every unit with a boundary.
   */
  const regionQuery = useQuery({
    queryKey: queryKeys.unitOptions('admin', 1, ext.country, null, false),
    queryFn: () => fetchUnitOptions('admin', 1, ext.country, null, false),
    enabled: onAdmin,
  });
  const regions = regionQuery.data ?? [];
  const regionId = pick(ext.region, regions, regionQuery.isSuccess);

  const communeQuery = useQuery({
    queryKey: queryKeys.unitOptions('admin', 2, ext.country, regionId || null, false),
    queryFn: () => fetchUnitOptions('admin', 2, ext.country, regionId || null, false),
    enabled: ext.kind === 'admin2' && Boolean(regionId),
  });
  const communes = communeQuery.data ?? [];
  const communeId = pick(ext.commune, communes, communeQuery.isSuccess);

  const flatQuery = useQuery({
    queryKey: queryKeys.unitOptions(flatType, null, ext.country, null, false),
    queryFn: () => fetchUnitOptions(flatType, null, ext.country, null, false),
    enabled: onFlat,
  });
  const flats = flatQuery.data ?? [];
  const flatId = pick(ext.unit, flats, flatQuery.isSuccess);

  /**
   * Can this rung name a unit yet? — T7 review 2.
   *
   * The first version widened silently to the country when a rung had nothing to offer,
   * which is an analysis over a different extent than the one on screen: a reader who
   * picked "Partnership" and pressed Run got the whole of Niger back, labelled Niger, and
   * nothing anywhere said why. So the two states are told apart and both stop the run — the
   * list has not arrived (`Run` disabled, nothing said, because there is nothing to say
   * yet), or it arrived empty for this country (`Run` disabled and the note says so).
   */
  const rung: { ready: boolean; empty: boolean } = !onAdmin && !onFlat
    ? { ready: true, empty: false }
    : onFlat
      ? { ready: flatQuery.isSuccess && flats.length > 0, empty: flatQuery.isSuccess && !flats.length }
      : !regionQuery.isSuccess
        ? { ready: false, empty: false }
        : regions.length === 0
          ? { ready: false, empty: true }
          : ext.kind === 'admin1'
            ? { ready: true, empty: false }
            : {
                ready: communeQuery.isSuccess && communes.length > 0,
                empty: communeQuery.isSuccess && !communes.length,
              };

  /** A rung change never carries a pick across: `WS01` is not an admin id (§H1's rule). */
  const changeKind = (kind: ExtentKind) =>
    setExt((current) => ({
      ...current,
      kind,
      commune: kind === 'admin2' ? current.commune : '',
      unit: '',
    }));

  /** Every list below is filtered by it, so every pick under it goes. */
  const changeCountry = (next: Country) =>
    setExt((current) => ({ ...current, country: next, region: '', commune: '', unit: '' }));

  /**
   * The extent as §E7 takes it — the four shapes `geom_from_params` resolves, most specific
   * first. Every unit branch here has a pick, because `rung.ready` is what let the button
   * be pressed at all.
   */
  const extentParams = (): Record<string, unknown> => {
    const whole = ext.country === 'ALL' ? {} : { country: ext.country };
    if (ext.kind === 'aoi') return aoi ? { aoi_polygon: aoiRing(aoi.pts) } : whole;
    if (ext.kind === 'admin1') return { unit_id: regionId };
    if (ext.kind === 'admin2') return { unit_id: communeId };
    if (onFlat) return { unit_id: flatId };
    return whole;
  };

  const submit = () => {
    if (!a || !b) {
      toast(t('analysis.change.noEpochs'));
      return;
    }
    if (a === b) {
      toast(t('analysis.change.sameEpoch'));
      return;
    }
    setRanKind(ext.kind);
    run.run({ epoch_a: a, epoch_b: b, ...extentParams() });
  };

  const result = run.result;
  const classes = result?.matrix.classes ?? [];

  /**
   * "Extent: Bale (Region)" — the server's own name for what it measured, plus the rung it
   * was picked on. The two extents §E7 names in English prose of its own are named here
   * instead, and say only their name (`SELF_NAMING_KIND`).
   */
  const extentLine = (extent: ExtentDescription): string => {
    const naming = SELF_NAMING_KIND[extent.kind];
    if (naming) return t('analysis.change.extentOnly', { name: t(naming) });
    const key = extent.kind === 'unit' ? KIND_KEY[ranKind] : RESULT_KIND_KEY[extent.kind];
    return t('analysis.change.extentLine', {
      name: extent.label,
      kind: key ? t(key) : extent.kind,
    });
  };

  const showOnMap = () => {
    const spec = result?.result_layers?.[0];
    if (!spec) return;
    addResultLayer(spec, {
      jobId: run.jobId ?? spec.id,
      tool: 'change',
      label: t('analysis.change.layerLabel', {
        a: result?.epochs.a.year ?? '',
        b: result?.epochs.b.year ?? '',
      }),
    });
    // Registering the layer is not the same as showing it: a result the reader asked to see
    // can be a hundred kilometres off the current view, and the button says "on map". The
    // fit is asked for every press, so a second press still takes them back to it — by then
    // the overlay is already registered and `ImageLayer`'s own once-per-result fit will not
    // fire again. On the *first* press both happen: `ImageLayer` mounts and fits, and this
    // request fits the same box a frame later. Two fits to one place is a no-op on screen,
    // and it is the cheap half of the trade against a button that sometimes does nothing.
    if (spec.bounds) fitBounds(spec.bounds);
    toast(t('analysis.change.added'));
  };

  return (
    <ToolFrame
      tool="change"
      onBack={onBack}
      phase={run.phase}
      progress={run.progress}
      error={run.error}
      cached={run.cached}
      result={
        result ? (
          <>
            <ResultBox
              title={t('analysis.change.matrix', {
                a: result.epochs.a.year ?? '',
                b: result.epochs.b.year ?? '',
              })}
              scroll
            >
              <table className="mini">
                <thead>
                  <tr>
                    <th>
                      {result.epochs.a.year} ↓ / {result.epochs.b.year} →
                    </th>
                    {classes.map((c) => (
                      <th key={c.value} className="num">
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {classes.map((rowClass, i) => (
                    <tr key={rowClass.value}>
                      <td>
                        <b>{rowClass.label}</b>
                      </td>
                      {classes.map((colClass, j) => {
                        const value = result.matrix.rows[i]?.[j] ?? 0;
                        const tint =
                          i === j ? 'mini-cell-diag' : value > WARM_THRESHOLD ? 'mini-cell-warm' : '';
                        return (
                          <td key={colClass.value} className={`num ${tint}`.trim()}>
                            {fmtNum(value, 1)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResultBox>

            <ResultBox
              title={t('analysis.change.net', {
                a: result.epochs.a.year ?? '',
                b: result.epochs.b.year ?? '',
              })}
            >
              <table className="mini">
                <tbody>
                  {result.net.map((row) => (
                    <tr key={row.class}>
                      <td>
                        <span className="status-dot" style={{ background: row.color }} />
                        {row.label}
                      </td>
                      <td className={`num ${row.delta_pct >= 0 ? 'delta-up' : 'delta-down'}`}>
                        {row.delta_pct > 0 ? '+' : ''}
                        {fmtNum(row.delta_pct, 1)}%
                      </td>
                      <td className="num" style={{ color: 'var(--ink-3)' }}>
                        {fmtKm2(Math.abs(row.km2), 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ResultBox>

            <ResultNote>
              {extentLine(result.extent)}
            </ResultNote>
            <ResultNote>
              {t('analysis.change.coverage', {
                pct: fmtNum(Math.min(100, result.coverage.pct), 1),
                km2: fmtKm2(result.coverage.analysed_km2),
                resolution: result.resolution_m ? Math.round(result.resolution_m) : '—',
              })}
            </ResultNote>
            {(result.notes ?? []).map((note) => (
              <ResultNote key={note}>{t(`analysis.note.${note}`, note)}</ResultNote>
            ))}

            {/* No layer means no pixel moved class. Said out loud rather than left as a
                missing button: an absent control reads as a feature that failed, and the
                reader has no way to tell the two apart from the tables alone. */}
            {result.result_layers?.length ? (
              <>
                {result.image ? (
                  <ResultNote>
                    {t('analysis.change.changedPct', {
                      pct: fmtNum(result.image.changed_pct, 1),
                    })}
                  </ResultNote>
                ) : null}
                <div className="ad-actions">
                  <button type="button" className="btn primary sm" id="chMap" onClick={showOnMap}>
                    <Icon name="map" size={12} /> {t('analysis.change.show')}
                  </button>
                </div>
              </>
            ) : (
              <ResultNote>{t('analysis.change.noImage')}</ResultNote>
            )}
          </>
        ) : null
      }
    >
      <label className="fld" htmlFor="chEpochA">
        {t('analysis.change.epochs')}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="std" id="chEpochA" value={a} onChange={(e) => setEpochA(e.target.value)}>
          {epochs.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
          {epochs.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
        </select>
        <span aria-hidden="true">→</span>
        <select className="std" id="chEpochB" value={b} onChange={(e) => setEpochB(e.target.value)}>
          {epochs.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
          {epochs.length === 0 ? <option value="">{t('analysis.noLayerOption')}</option> : null}
        </select>
      </div>

      <label className="fld" htmlFor="chExt">
        {t('analysis.change.extent')}
      </label>
      <select
        className="std"
        id="chExt"
        value={ext.kind}
        onChange={(e) => changeKind(e.target.value as ExtentKind)}
      >
        {EXTENT_KINDS.map((kind) => (
          // The drawn area stays on the list with nothing drawn, disabled: an option that
          // vanishes reads as a feature that is missing, one that is greyed reads as a step
          // not taken yet.
          <option key={kind} value={kind} disabled={kind === 'aoi' && !aoi}>
            {t(KIND_KEY[kind])}
          </option>
        ))}
      </select>

      {ext.kind !== 'aoi' ? (
        <select
          className="std"
          id="chExtCountry"
          aria-label={t('hierarchy.filter.country')}
          value={ext.country}
          onChange={(e) => changeCountry(e.target.value as Country)}
        >
          {EXTENT_COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {c === 'ALL' ? t('analysis.change.programme') : t(`country.${c}`, COUNTRY_NAMES[c])}
            </option>
          ))}
        </select>
      ) : null}

      {onAdmin ? (
        <UnitSelect
          id="chExtRegion"
          label={t('indicators.levelRegion')}
          value={regionId}
          options={regions}
          onPick={(id) => setExt((current) => ({ ...current, region: id, commune: '' }))}
        />
      ) : null}

      {ext.kind === 'admin2' ? (
        <UnitSelect
          id="chExtCommune"
          label={t('indicators.levelCommune')}
          value={communeId}
          options={communes}
          onPick={(id) => setExt((current) => ({ ...current, commune: id }))}
        />
      ) : null}

      {onFlat ? (
        <UnitSelect
          id="chExtUnit"
          label={t(KIND_KEY[ext.kind])}
          value={flatId}
          options={flats}
          onPick={(id) => setExt((current) => ({ ...current, unit: id }))}
        />
      ) : null}

      {/* Only once the list has actually answered: "no units" printed over a request still
          in flight is a falsehood with a half-second life, and the disabled button already
          says "not yet" for that case. */}
      {rung.empty ? <ResultNote>{t('analysis.change.noUnitsHere')}</ResultNote> : null}

      <button
        type="button"
        className="btn primary ad-run"
        id="chRun"
        disabled={run.phase === 'running' || !rung.ready}
        onClick={submit}
      >
        <Icon name="play" size={13} /> {t('analysis.change.run')}
      </button>
    </ToolFrame>
  );
}

/**
 * The caller's pick if it is still on offer, else the first row, else nothing — `pickers`'
 * `firstAvailable` rule for org units rather than layers. A select left pointing at a unit
 * the list no longer holds is a form that submits an id the hierarchy has moved on from.
 *
 * `loaded` is the **query's** answer, not `options.length` (T7 review 2): an empty array is
 * both "still in flight" and "this country has none of these", and those two must not be
 * the same thing here — while the list is in flight a scope-derived id is still the best
 * pick there is, and once it has arrived empty there is no pick at all.
 */
function pick(preferred: string, options: readonly UnitOption[], loaded: boolean): string {
  if (preferred && options.some((o) => o.id === preferred)) return preferred;
  if (!loaded) return preferred;
  return options[0]?.id ?? '';
}

/**
 * One rung of the cascade. An empty list draws an empty select and nothing else — the
 * `noUnitsHere` note under the picker is where that is said, once, rather than in a
 * disabled option saying the same thing a second time.
 */
function UnitSelect({
  id,
  label,
  value,
  options,
  onPick,
}: {
  id: string;
  label: string;
  value: string;
  options: readonly UnitOption[];
  onPick: (id: string) => void;
}) {
  return (
    <select
      className="std"
      id={id}
      aria-label={label}
      value={value}
      onChange={(e) => onPick(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
}

export default Change;
